import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axios';

const isBrowser = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';
const hasNotificationAPI = isBrowser && 'Notification' in window;
const hasServiceWorker = hasNavigator && 'serviceWorker' in navigator;
const hasPushManager = isBrowser && 'PushManager' in window;

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

interface PushSubscriptionState {
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
}

export const usePushNotifications = () => {
  const isSupported = hasServiceWorker && hasPushManager;
  
  console.log('[PUSH_FRONTEND] usePushNotifications inicializado', {
    isSupported,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    vapidKeyPresent: !!VAPID_PUBLIC_KEY,
    vapidKeyLength: VAPID_PUBLIC_KEY.length,
    apiUrl: process.env.REACT_APP_API_URL
  });

  const [state, setState] = useState<PushSubscriptionState>({
    isSupported,
    isSubscribed: false,
    isLoading: false,
    error: null,
  });

  const checkSubscription = useCallback(async () => {
    console.log('[PUSH_FRONTEND] checkSubscription iniciado', { isSupported: state.isSupported });
    
    if (!state.isSupported) {
      console.warn('[PUSH_FRONTEND] Push notifications não suportadas, abortando checkSubscription');
      return;
    }

    try {
      // Verificar se já existe um service worker registrado
      console.log('[PUSH_FRONTEND] Verificando service workers existentes...');
      const existingRegistrations = await navigator.serviceWorker.getRegistrations();
      console.log('[PUSH_FRONTEND] Service workers encontrados', { count: existingRegistrations.length });
      
      let registration = existingRegistrations.length > 0 ? existingRegistrations[0] : null;
      
      if (!registration) {
        console.log('[PUSH_FRONTEND] Nenhum service worker registrado, registrando...');
        try {
          registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
          console.log('[PUSH_FRONTEND] ✅ Service worker registrado', { 
            scope: registration.scope,
            installing: !!registration.installing,
            waiting: !!registration.waiting,
            active: !!registration.active
          });
        } catch (regError: any) {
          console.error('[PUSH_FRONTEND] ❌ Erro ao registrar service worker', {
            error: regError.message,
            stack: regError.stack,
            name: regError.name
          });
          setState((prev) => ({ ...prev, isSubscribed: false }));
          return;
        }
      } else {
        console.log('[PUSH_FRONTEND] Service worker já registrado', { 
          scope: registration.scope,
          installing: !!registration.installing,
          waiting: !!registration.waiting,
          active: !!registration.active
        });
      }

      // Aguardar service worker ficar ready com timeout
      console.log('[PUSH_FRONTEND] Aguardando service worker ready (timeout: 10s)...');
      const readyPromise = navigator.serviceWorker.ready;
      const timeoutPromise = new Promise<ServiceWorkerRegistration>((_, reject) => 
        setTimeout(() => reject(new Error('Timeout aguardando service worker ready após 10 segundos')), 10000)
      );
      
      let activeRegistration: ServiceWorkerRegistration;
      try {
        activeRegistration = await Promise.race([readyPromise, timeoutPromise]);
        console.log('[PUSH_FRONTEND] ✅ Service worker ready', { 
          scope: activeRegistration.scope,
          active: !!activeRegistration.active
        });
      } catch (timeoutError: any) {
        console.warn('[PUSH_FRONTEND] ⚠️ Timeout aguardando service worker ready', {
          error: timeoutError.message,
          registrationState: registration.active ? 'active' : registration.waiting ? 'waiting' : registration.installing ? 'installing' : 'unknown'
        });
        // Tentar usar a registration existente mesmo se não estiver ready
        activeRegistration = registration;
      }
      
      console.log('[PUSH_FRONTEND] Verificando subscription existente...');
      const subscription = await activeRegistration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('[PUSH_FRONTEND] ✅ Subscription encontrada', {
          endpoint: subscription.endpoint.substring(0, 50) + '...',
          hasP256dh: !!subscription.getKey('p256dh'),
          hasAuth: !!subscription.getKey('auth')
        });
      } else {
        console.log('[PUSH_FRONTEND] Nenhuma subscription encontrada');
      }
      
      setState((prev) => ({ ...prev, isSubscribed: !!subscription }));
    } catch (error: any) {
      console.error('[PUSH_FRONTEND] ❌ Erro ao verificar subscription', {
        error: error.message,
        stack: error.stack,
        name: error.name
      });
      // Em caso de erro, assumir que não está inscrito
      setState((prev) => ({ ...prev, isSubscribed: false }));
    }
  }, [state.isSupported]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    console.log('[PUSH_FRONTEND] requestPermission iniciado', { isSupported: state.isSupported });
    
    if (!state.isSupported) {
      console.warn('[PUSH_FRONTEND] Push notifications não suportadas');
      setState((prev) => ({
        ...prev,
        error: 'Push notifications não são suportadas neste navegador',
      }));
      return false;
    }

    if (!hasNotificationAPI) {
      console.warn('[PUSH_FRONTEND] API Notification indisponível neste navegador');
      setState((prev) => ({
        ...prev,
        error: 'Notificações não são suportadas neste dispositivo',
      }));
      return false;
    }

    try {
      const currentPermission = getNotificationPermission();
      console.log('[PUSH_FRONTEND] Permissão atual', { permission: currentPermission });
      
      const permission = await requestNotificationPermission();
      console.log('[PUSH_FRONTEND] Resposta de permissão', { permission });
      
      const granted = permission === 'granted';
      console.log(granted ? '[PUSH_FRONTEND] ✅ Permissão concedida' : '[PUSH_FRONTEND] ❌ Permissão negada', { permission });
      
      return granted;
    } catch (error: any) {
      console.error('[PUSH_FRONTEND] ❌ Erro ao solicitar permissão', {
        error: error.message,
        stack: error.stack,
        name: error.name
      });
      setState((prev) => ({
        ...prev,
        error: 'Erro ao solicitar permissão',
      }));
      return false;
    }
  }, [state.isSupported]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    console.log('[PUSH_FRONTEND] subscribe iniciado', { isSupported: state.isSupported, vapidKeyPresent: !!VAPID_PUBLIC_KEY });
    
    if (!state.isSupported) {
      console.warn('[PUSH_FRONTEND] Push notifications não suportadas');
      setState((prev) => ({
        ...prev,
        error: 'Push notifications não são suportadas',
      }));
      return false;
    }

    if (!hasNotificationAPI) {
      console.warn('[PUSH_FRONTEND] API Notification indisponível neste navegador');
      setState((prev) => ({
        ...prev,
        error: 'Notificações não são suportadas neste dispositivo',
      }));
      return false;
    }

    if (!VAPID_PUBLIC_KEY) {
      console.error('[PUSH_FRONTEND] ❌ VAPID_PUBLIC_KEY não configurada');
      setState((prev) => ({
        ...prev,
        error: 'VAPID key não configurada',
      }));
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Primeiro, verificar se já temos permissão
      let permission = getNotificationPermission();
      console.log('[PUSH_FRONTEND] Permissão inicial', { permission });
      
      // Se não tiver permissão, solicitar (isso vai mostrar o popup do navegador)
      if (permission === 'default') {
        console.log('[PUSH_FRONTEND] Solicitando permissão...');
        permission = await requestNotificationPermission();
        console.log('[PUSH_FRONTEND] Permissão recebida', { permission });
      }
      
      if (permission !== 'granted') {
        console.warn('[PUSH_FRONTEND] ❌ Permissão não concedida', { permission });
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: permission === 'denied' 
            ? 'Permissão negada. Por favor, permita notificações nas configurações do navegador.'
            : 'Permissão negada para notificações',
        }));
        return false;
      }

      console.log('[PUSH_FRONTEND] Registrando/obtendo service worker...');
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      console.log('[PUSH_FRONTEND] Service worker registrado', { scope: registration.scope });
      
      console.log('[PUSH_FRONTEND] Aguardando service worker ready...');
      const activeRegistration = await navigator.serviceWorker.ready;
      console.log('[PUSH_FRONTEND] Service worker ready');

      if (!activeRegistration.pushManager) {
        throw new Error('PushManager não disponível no service worker ativo');
      }

      console.log('[PUSH_FRONTEND] Verificando subscription existente antes de criar nova...');
      let subscription = await activeRegistration.pushManager.getSubscription();

      if (subscription) {
        console.log('[PUSH_FRONTEND] ♻️ Subscription existente reutilizada', {
          endpoint: subscription.endpoint.substring(0, 50) + '...',
          hasP256dh: !!subscription.getKey('p256dh'),
          hasAuth: !!subscription.getKey('auth')
        });
      } else {
        console.log('[PUSH_FRONTEND] Nenhuma subscription existente. Criando nova...');
        console.log('[PUSH_FRONTEND] Convertendo VAPID key...', { vapidKeyLength: VAPID_PUBLIC_KEY.length });
        const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        console.log('[PUSH_FRONTEND] VAPID key convertida', { keyLength: applicationServerKey.length });

        subscription = await activeRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey as BufferSource,
        });
        console.log('[PUSH_FRONTEND] ✅ Subscription criada', {
          endpoint: subscription.endpoint.substring(0, 50) + '...',
          hasP256dh: !!subscription.getKey('p256dh'),
          hasAuth: !!subscription.getKey('auth')
        });
      }

      // Enviar subscription para o backend
      const subscriptionData = serializeSubscription(subscription);

      console.log('[PUSH_FRONTEND] Enviando subscription para backend...', {
        apiUrl: process.env.REACT_APP_API_URL,
        endpoint: subscriptionData.endpoint.substring(0, 50) + '...',
        hasP256dh: !!subscriptionData.keys.p256dh,
        hasAuth: !!subscriptionData.keys.auth,
        p256dhLength: subscriptionData.keys.p256dh.length,
        authLength: subscriptionData.keys.auth.length
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/push_subscriptions`,
        { subscription: subscriptionData }
      );

      console.log('[PUSH_FRONTEND] ✅ Subscription enviada com sucesso', {
        status: response.status,
        data: response.data
      });

      setState((prev) => ({
        ...prev,
        isSubscribed: true,
        isLoading: false,
      }));

      console.log('[PUSH_FRONTEND] ✅ Subscribe completo com sucesso');
      return true;
    } catch (error: any) {
      console.error('[PUSH_FRONTEND] ❌ Erro ao fazer subscribe', {
        error: error.message,
        stack: error.stack,
        name: error.name,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        } : null
      });
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || 'Erro ao se inscrever nas notificações',
      }));
      return false;
    }
  }, [state.isSupported]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    console.log('[PUSH_FRONTEND] unsubscribe iniciado', { isSupported: state.isSupported });
    
    if (!state.isSupported) {
      console.warn('[PUSH_FRONTEND] Push notifications não suportadas');
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      console.log('[PUSH_FRONTEND] Aguardando service worker ready...');
      const registration = await navigator.serviceWorker.ready;
      console.log('[PUSH_FRONTEND] Verificando subscription existente...');
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        console.log('[PUSH_FRONTEND] Subscription encontrada, removendo...', {
          endpoint: subscription.endpoint.substring(0, 50) + '...'
        });
        
        // Remover do backend
        console.log('[PUSH_FRONTEND] Removendo subscription do backend...');
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/push_subscriptions`,
          {
            params: { endpoint: subscription.endpoint },
          }
        );
        console.log('[PUSH_FRONTEND] ✅ Subscription removida do backend');

        // Remover subscription local
        console.log('[PUSH_FRONTEND] Removendo subscription local...');
        await subscription.unsubscribe();
        console.log('[PUSH_FRONTEND] ✅ Subscription local removida');

        setState((prev) => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
        }));

        console.log('[PUSH_FRONTEND] ✅ Unsubscribe completo com sucesso');
        return true;
      } else {
        console.log('[PUSH_FRONTEND] Nenhuma subscription encontrada para remover');
      }
    } catch (error: any) {
      console.error('[PUSH_FRONTEND] ❌ Erro ao fazer unsubscribe', {
        error: error.message,
        stack: error.stack,
        name: error.name,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data
        } : null
      });
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || 'Erro ao cancelar inscrição',
      }));
    }

    return false;
  }, [state.isSupported]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    requestPermission,
    checkSubscription,
  };
};

// Helper functions
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function getNotificationPermission(): NotificationPermission {
  if (!hasNotificationAPI) {
    return 'denied';
  }
  return Notification.permission;
}

async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!hasNotificationAPI) {
    return 'denied';
  }

  // Safari < 16 usa callback, navegadores modernos retornam Promise
  if (Notification.requestPermission.length === 1) {
    return new Promise<NotificationPermission>((resolve, reject) => {
      try {
        Notification.requestPermission((permission) => resolve(permission));
      } catch (error) {
        reject(error);
      }
    });
  }

  return Notification.requestPermission();
}

function serializeSubscription(subscription: PushSubscription) {
  const p256dhBuffer = subscription.getKey('p256dh');
  const authBuffer = subscription.getKey('auth');

  if (!p256dhBuffer || !authBuffer) {
    throw new Error('Subscription inválida: chaves não informadas');
  }

  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(p256dhBuffer),
      auth: arrayBufferToBase64(authBuffer),
    },
  };
}

